import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { ProductAttributeSelectorProps } from '../../config.types';
import { useGetAttributeDataForSelector } from '../hooks/getAttributeDataForSelector';
import { Box, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { styled } from '@withjoy/joykit/theme';
import React from 'react';
import { CheckIconBold } from '@assets/icons';
import { Info } from '@withjoy/joykit/icons';
import { HighlightedValuePropGroup, ValuePropLineItem } from './DigitalDeliveryAttribute.styles';
import { setAttributeStateWithValidation } from '../../utils/state/setAttributeStateWithValidation';
import { useProductAttribute } from '../../useProductAttribute';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { animationTransition } from '@shared/utils/animationTransition';
import type { ValueProp, ValuePropNote } from '../../product.types';
import { groupValueProps, isHighlightedValueProp, valuePropLabel } from './groupValueProps';

const RegionList = styled.ul`
  margin: 0;
  padding-left: ${props => props.theme.space[4]};
  list-style-type: disc;
`;

const ValuePropNoteTrigger = ({ note }: { note: ValuePropNote }) => (
  // Stop the click here so opening the note does not also toggle the delivery option.
  <Box
    as="span"
    display="inline-flex"
    onClick={e => e.stopPropagation()}
  >
    <TooltipV2
      triggerType="click"
      placement="bottom"
      usePortal
      closeOnOutsideClick
      overrides={{ Content: { props: { maxWidth: '280px', paddingX: 5, paddingY: 4 } } }}
      content={
        <Flex
          flexDirection="column"
          rowGap={2}
        >
          <TextV2 typographyVariant="label3">{note.title}</TextV2>
          <RegionList>
            {note.items.map(item => (
              <TextV2
                key={item}
                tagName="li"
                typographyVariant="label2"
              >
                {item}
              </TextV2>
            ))}
          </RegionList>
        </Flex>
      }
    >
      <Box
        as="button"
        type="button"
        aria-label={note.ariaLabel}
        display="inline-flex"
        alignItems="center"
        padding={0}
        border="none"
        backgroundColor="transparent"
        color="mono10"
        cursor="pointer"
        _hover={{ color: 'mono14' }}
        _focusVisible={{ color: 'mono14', outline: '2px solid', outlineColor: 'inputBorderFocused', outlineOffset: '2px' }}
      >
        <Info size={16} />
      </Box>
    </TooltipV2>
  </Box>
);

const ValuePropRow = ({ prop }: { prop: ValueProp }) => {
  const note = typeof prop === 'string' ? undefined : prop.note;

  return (
    <ValuePropLineItem
      $highlight={isHighlightedValueProp(prop)}
      typographyVariant="label1"
      display="flex"
      alignItems="center"
      columnGap={2}
    >
      {valuePropLabel(prop)}
      {note && <ValuePropNoteTrigger note={note} />}
    </ValuePropLineItem>
  );
};

export const DigitalDeliveryAttribute = (props: ProductAttributeSelectorProps) => {
  const { product, attributeId, priceCalculator } = props;
  const data = useGetAttributeDataForSelector(props);
  const formatPrice = useMinorUnitsToPriceString();
  const attribute = useProductAttribute(attributeId);
  const { tq } = useTranslation('stationery');
  const [showSmsDeliveryStat] = useIsFeatureEnabled('ecardSmsDeliveryStat');

  if (!data || !data.options.selected) {
    return null;
  }

  const uiConfig = data.attribute.attributeUIConfig;
  const quantity = Number(data.draftData.digitalCustomizations?.quantity ?? 100);

  const options = data.options.uiConfigurations;
  const selectedOptionId = data.options.selected.optionId;

  return (
    <Flex
      flexDirection="column"
      rowGap={6}
    >
      <TextV2 typographyVariant="hed3">{uiConfig.title}</TextV2>
      {uiConfig.subtitle && <TextV2 typographyVariant="body1">{uiConfig.subtitle}</TextV2>}
      <Flex
        flexDirection="column"
        rowGap={5}
      >
        {options.map(option => {
          const isSelected = option.optionId === selectedOptionId;
          const optionPrice = priceCalculator.getOptionPrice(product.id, { attributeId, optionId: option.optionId }, quantity);
          const formattedOptionPrice = formatPrice(optionPrice, { canShowFreeLabel: true, freeLabel: tq`Free`, precision: 0 });
          const baseValueProps = attribute?.options.find(opt => opt.optionId === option.optionId)?.valueProps ?? [];
          const valueProps =
            showSmsDeliveryStat && (option.optionId === 'premium' || option.optionId === 'diamond')
              ? [...baseValueProps, { label: tq`7x better open rate than email`, highlight: option.optionId === 'premium' }]
              : baseValueProps;

          return (
            <Flex
              alignItems="flex-start"
              key={option.optionId}
              paddingX={5}
              paddingY="20px"
              borderRadius="12px"
              border="2px solid"
              borderColor={isSelected ? 'mono14' : 'mono5'}
              transition={animationTransition('border-color')}
              cursor="pointer"
              onClick={() => {
                setAttributeStateWithValidation(props, data, attributeId, option.optionId);
              }}
            >
              <Flex
                justifyContent="center"
                alignItems="center"
                minWidth="24px"
                height="24px"
                borderRadius="12px"
                border="1px solid"
                borderColor={isSelected ? 'mono14' : 'mono5'}
                backgroundColor={isSelected ? 'mono14' : 'white'}
                transition={animationTransition('background-color', 'border-color')}
                color="white"
              >
                <CheckIconBold width={18} />
              </Flex>
              <Flex
                marginLeft={5}
                flexDirection="column"
                rowGap={3}
                flex={1}
              >
                <Flex
                  justifyContent="space-between"
                  columnGap={3}
                >
                  {option.titleLines ? (
                    <Flex flexDirection="column">
                      {option.titleLines.map(line => (
                        <TextV2
                          key={line}
                          typographyVariant="button1"
                          whiteSpace="nowrap"
                        >
                          {line}
                        </TextV2>
                      ))}
                    </Flex>
                  ) : (
                    <TextV2 typographyVariant="button1">{option.title}</TextV2>
                  )}
                  <TextV2
                    typographyVariant="button1"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {optionPrice > 0 ? `+${formattedOptionPrice}` : formattedOptionPrice}
                  </TextV2>
                </Flex>
                {groupValueProps(valueProps).map(group => {
                  const key = valuePropLabel(group.valueProps[0]);
                  const rows = group.valueProps.map(prop => (
                    <ValuePropRow
                      key={valuePropLabel(prop)}
                      prop={prop}
                    />
                  ));

                  return group.highlight ? <HighlightedValuePropGroup key={key}>{rows}</HighlightedValuePropGroup> : <React.Fragment key={key}>{rows}</React.Fragment>;
                })}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};
